import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "../../../../environments/environment";

export interface ConversationAttachmentAsset {
  id: number | string;
  name: string;
  type: string;
}

export interface ConversationAttachmentResponse {
  id: string;
  uploadedAt: string;
  assets: ConversationAttachmentAsset[];
}

/**
 * Wraps the existing {@code POST /conversations/{id}/attachment} endpoint for widgets
 * that need to upload files inline before submitting a component response. Returns the
 * parsed {@link ConversationAttachmentResponse} so callers can keep the outer
 * ConversationAttachment id (never the inner asset ids) for later submission.
 */
@Injectable({ providedIn: "root" })
export class ConversationAttachmentService {
  constructor(private _http: HttpClient) {}

  uploadAttachment(
    conversationContextId: string,
    file: File
  ): Observable<ConversationAttachmentResponse> {
    return this.uploadAttachments(conversationContextId, [file]);
  }

  /** Upload one or more files in a single request (one conversation_attachment row). */
  uploadAttachments(
    conversationContextId: string,
    files: File[]
  ): Observable<ConversationAttachmentResponse> {
    const formData = new FormData();
    for (const file of files) {
      if (file) {
        formData.append("files", file, file.name);
      }
    }
    const url = `conversations/${conversationContextId}/attachment`;
    return this._http.post<ConversationAttachmentResponse>(url, formData);
  }

  /**
   * OD5: conversation-scoped asset fetch. Replaces bare /assets/{id}.
   * The interceptor injects X-Tenant-Code on paths that don't start with '/assets/',
   * so this new path automatically receives the dealer header — no interceptor change needed.
   */
  fetchAssetBlob(conversationContextId: string, assetId: number | string): Observable<Blob> {
    return this._http.get(
      `conversations/${conversationContextId}/attachments/${assetId}`,
      { responseType: "blob" }
    );
  }

  buildAssetObjectUrl(conversationContextId: string, assetId: number | string): Observable<string> {
    return this.fetchAssetBlob(conversationContextId, assetId).pipe(
      map((blob) => URL.createObjectURL(blob))
    );
  }
}
